/**
 * Objection Response Modal Component
 * Modal for creating and editing objection responses
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { ObjectionResponse } from '@/lib/types/call-center-settings';

interface ObjectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<ObjectionResponse>) => Promise<void>;
  objection?: ObjectionResponse | null;
  companyId: string;
}

export function ObjectionModal({ isOpen, onClose, onSave, objection, companyId }: ObjectionModalProps) {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    objection_text: '',
    response_template: '',
    category: 'price',
    effectiveness_score: null as number | null
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (objection) {
      setFormData({
        objection_text: objection.objection_text,
        response_template: objection.response_template,
        category: objection.category,
        effectiveness_score: objection.effectiveness_score
      });
    } else {
      setFormData({
        objection_text: '',
        response_template: '',
        category: 'price',
        effectiveness_score: null
      });
    }
  }, [objection, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...formData,
        company_id: companyId
      });
      onClose();
    } catch (error) {
      console.error('Error saving objection response:', error);
      alert('Failed to save objection response');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: theme.colors.bgCard }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold mb-4" style={{ color: theme.colors.textPrimary }}>
          {objection ? 'Edit Objection Response' : 'New Objection Response'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Objection Text *
            </label>
            <input
              type="text"
              required
              value={formData.objection_text}
              onChange={(e) => setFormData({ ...formData, objection_text: e.target.value })}
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
              placeholder="e.g., This is too expensive"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Category *
            </label>
            <select
              required
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
            >
              <option value="price">Price</option>
              <option value="hesitation">Hesitation</option>
              <option value="competition">Competition</option>
              <option value="coverage">Coverage</option>
              <option value="timing">Timing</option>
              <option value="trust">Trust</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Response Template *
            </label>
            <textarea
              required
              value={formData.response_template}
              onChange={(e) => setFormData({ ...formData, response_template: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
              placeholder="Enter the suggested response template..."
            />
            <p className="text-xs mt-1" style={{ color: theme.colors.textSecondary }}>
              Use placeholders like [customer name] or [product] that agents can customize
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Effectiveness Score (optional)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="5"
              value={formData.effectiveness_score || ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                effectiveness_score: e.target.value ? parseFloat(e.target.value) : null 
              })}
              className="w-32 px-3 py-2 rounded border"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
              placeholder="0-5"
            />
            <p className="text-xs mt-1" style={{ color: theme.colors.textSecondary }}>
              Rate 0-5 based on how effective this response has been
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded font-medium"
              style={{
                backgroundColor: theme.colors.bgHover,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded font-medium"
              style={{
                backgroundColor: theme.colors.primaryColor,
                color: '#ffffff'
              }}
            >
              {saving ? 'Saving...' : (objection ? 'Update Response' : 'Create Response')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
